import React, { useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../config";

// const API_BASE_URL = "https://screening.fanisko.com";
// const API_BASE_URL = "http://127.0.0.1:8000";

function cleanInterviewMetadata(text) {
  if (!text) return "";

  let t = text;

  // Removes the FIRST ``` ... ``` fenced block (just like Python)
  t = t.replace(/```[\s\S]*?```/, "").trim();

  // Removes unfenced <interview_state> ... </interview_state> (fallback)
  t = t.replace(/<interview_state[\s\S]*?<\/interview_state>/i, "").trim();

  return t;
}




function extractQuestion(text) {
  if (!text) return "";
  if (text.includes("<<INTERVIEW_COMPLETE>>")) return "";
  return text.trim();
}

export default function ChatbotInterface({ userName = "Candidate", job = {}, candidateId }) {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]); // each {question, answer}
  const [currentIndex, setCurrentIndex] = useState(0);
  const [response, setResponse] = useState("");
  const [interviewComplete, setInterviewComplete] = useState(false);
  const [finalReport, setFinalReport] = useState(null);
  // const [autoAdvancedIndex, setAutoAdvancedIndex] = useState(null);

  // Add 3-min timer state
  const [timeLeft, setTimeLeft] = useState(180);
  const [extraTimeGiven, setExtraTimeGiven] = useState(false); // Track if extra minute granted

  const [sending, setSending] = useState(false);

  const timerRef = useRef(null); // To keep interval between renders
  const autoAdvanceRef = useRef(false);

  const responseRef = useRef("");
  const questionsRef = useRef([]);

  const inputRef = useRef(null);
  const jobId = job.jobid;

  useEffect(() => {
    async function fetchFirstQuestion() {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/generate_questions?candidate_id=${candidateId}&job_id=${jobId}`
        );
        const data = await res.json();
        const rawFirst = data.questions && data.questions[0] ? data.questions[0] : "";
        const firstQ = extractQuestion(cleanInterviewMetadata(rawFirst));
        const initialQs = [{ question: firstQ, answer: "" }];
        setQuestions(initialQs);
        questionsRef.current = initialQs;

        setCurrentIndex(0);
        setInterviewComplete(false);
        setFinalReport(null);
        setResponse("");
        responseRef.current = "";
        setExtraTimeGiven(false);
        setTimeLeft(180);
        autoAdvanceRef.current = false;
      } catch (error) {
        console.error("Error fetching first question:", error);
        setQuestions([]);
        questionsRef.current = [];
      }
      setLoading(false);
    }
    if (candidateId && jobId) fetchFirstQuestion();
  }, [candidateId, jobId]);

  // Focus input and update response on question change
  useEffect(() => {
    if (inputRef.current && !interviewComplete) inputRef.current.focus();

    if (!interviewComplete) {
      const ans = questions[currentIndex]?.answer || "";
      setResponse(ans);
      responseRef.current = ans;
      setExtraTimeGiven(false);
    }
  }, [currentIndex, questions, interviewComplete]);

  // timer effect
  useEffect(() => {
    if (interviewComplete) return;
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        console.log(
          '[TIMER TICK]',
          'prev:', prev,
          'currentIndex:', currentIndex,
          'extraTimeGiven:', extraTimeGiven,
          'autoAdvanceRef:', autoAdvanceRef.current,
          'interviewComplete:', interviewComplete
        );
        const currentQuestions = questionsRef.current;
        const currentResponse = responseRef.current.trim();
        const currentQ = currentQuestions[currentIndex];

        // auto "Timeout - No answer" at 90s
        if (
          prev === 90 &&
          !autoAdvanceRef.current &&
          currentQ &&
          !currentQ.answer &&
          currentResponse === ""
        ) {
          autoAdvanceRef.current = true;
          setQuestions((prevQuestions) => {
            const updated = [...prevQuestions];
            updated[currentIndex].answer = "Timeout - No answer";
            questionsRef.current = updated;
            return updated;
          });
          setResponse("Timeout - No answer");
          responseRef.current = "Timeout - No answer";

          setTimeout(() => {
            handleNext();
          }, 300);

          clearInterval(timerRef.current);
          return prev;
        }

        // extra minute or auto-next at 0
        if (prev === 0) {
          console.log(
            '[TIMER] prev===0, extraTimeGiven:',
            extraTimeGiven,
            'currentResponse.length:',
            currentResponse.length,
            'interviewComplete:',
            interviewComplete
          );
          if (!extraTimeGiven && currentResponse.length >= 30) {
            console.log('[TIMER] ✅ Granting extra 1 minute');
            setExtraTimeGiven(true);
            return 60;
          } else {
            console.log('[TIMER] ❌ No extra time. Auto-nexting.');
            clearInterval(timerRef.current);
            if (!interviewComplete) {
              console.log('[TIMER] Calling handleNext() from timer=0');
              handleNext();
            }
            return 0;
          }
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [interviewComplete, currentIndex, extraTimeGiven]);

  const handleNext = async () => {
    if (interviewComplete) return;

    setExtraTimeGiven(false);

    const updatedQuestions = [...questionsRef.current];
    const trimmedResponse = responseRef.current.trim();
    updatedQuestions[currentIndex].answer =
      trimmedResponse !== "" ? trimmedResponse : updatedQuestions[currentIndex].answer || "";

    const qText = updatedQuestions[currentIndex].question;
    const aText = updatedQuestions[currentIndex].answer;

    console.log("[SEND]", "qIndex:", currentIndex, "question:", qText, "storedAnswer:", aText);

    setQuestions(updatedQuestions);
    questionsRef.current = updatedQuestions;

    // if next question already exists, just move
    if (currentIndex < updatedQuestions.length - 1 && updatedQuestions[currentIndex + 1].question) {
      setCurrentIndex(currentIndex + 1);
      setTimeLeft(180);
      autoAdvanceRef.current = false;
      return;
    }

    try {
      console.log("[handleNext] sending /api/send_message");
      setSending(true);
      const res = await fetch(`${API_BASE_URL}/api/send_message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          candidate_id: candidateId,
          job_id: jobId,
          message: aText,
        }),
      });
      const data = await res.json();
      console.log("[RECV]", "completed:", data.completed, "ai_message:", data.ai_message);

      const completionDetected =
        data.completed === true ||
        (data.ai_message && data.ai_message.includes("<<INTERVIEW_COMPLETE>>"));
      if (completionDetected) {
        setInterviewComplete(true);
        setFinalReport(data.report || null);
        return;
      }

      const rawNext = data.ai_message || "";
      const nextQ = extractQuestion(cleanInterviewMetadata(rawNext));
      console.log("[Q] APPEND", "index:", currentIndex + 1, "question:", nextQ);

      const newQs = [...updatedQuestions, { question: nextQ, answer: "" }];
      setQuestions(newQs);
      questionsRef.current = newQs;

      setCurrentIndex(currentIndex + 1);
      setTimeLeft(180);
      autoAdvanceRef.current = false;
      setResponse("");
      responseRef.current = "";
    } catch (err) {
      console.error("[handleNext] Error sending message:", err);
    } finally {
      setSending(false);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const timerBoxStyle = {
    padding: "4px 8px",
    border: "1px solid #224979",
    borderRadius: "4px",
    backgroundColor: "#fff",
    fontSize: "14px",
    fontWeight: 600,
    display: "inline-block",
  };

  const styles = {
    root: {
      minHeight: "100vh",
      background: "#eaf3fb",
      display: "flex",
      flexDirection: "column",
      fontFamily: "Segoe UI, Roboto, Arial, sans-serif",
      width: "100%",
      overflow: "hidden",
    },
    header: {
      width: "100%",
      minHeight: 64,
      background: "#fff",
      borderBottom: "1.6px solid #dde6f2",
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-start",
      justifyContent: "space-between",
      position: "sticky",
      top: 0,
      zIndex: 20,
      padding: "18px 0 8px 34px",
    },
    headerLeft: {
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 14,
    },
    avatar: {
      fontSize: 26,
      background: "#f1f8ff",
      borderRadius: "50%",
      width: 39,
      height: 39,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    userName: {
      fontWeight: 700,
      fontSize: "1.11rem",
      color: "#173c49",
      lineHeight: "21px",
    },
    jobLabel: {
      fontWeight: 500,
      color: "#4371bb",
      fontSize: 13,
      marginTop: 2,
    },
    stickyQInfoBar: {
      background: "#f9fafc",
      padding: "12px 50px",
      borderBottom: "1.2px solid #e6eaf5",
      position: "sticky",
      top: 64,
      zIndex: 15,
      display: "flex",
      alignItems: "center",
      minHeight: 38,
      boxSizing: "border-box",
    },
    stickyQuestionCount: {
      fontWeight: 600,
      fontSize: 15,
      color: "#224979",
      letterSpacing: "0.01em",
    },
    progressBarArea: {
      padding: "24px 56px 0 0",
      display: "flex",
      alignItems: "flex-start",
    },
    progressBar: {
      background: "#e1eefa",
      height: 10,
      borderRadius: 8,
      overflow: "hidden",
      minWidth: 120,
      transition: "width .25s",
    },
    progressFill: {
      background: "linear-gradient(90deg, #7faaff 10%, #22ba73 80%)",
      height: "100%",
      borderRadius: 8,
      transition: "width 0.4s",
    },
    sendBtn: {
      border: "none",
      outline: "none",
      background: "#2684ff",
      color: "#fff",
      cursor: "pointer",
      marginRight: 0,
      marginLeft: 0,
      padding: "12px 28px",
      borderRadius: 6,
      opacity: 1,
      transition: "opacity 0.17s, background 0.17s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 6px rgba(38,132,255,0.15)",
      minWidth: 100,
      fontSize: "1.07rem",
      fontWeight: 500,
      height: 44,
    },
  };

  const isLowTime = timeLeft < 60;

  if (loading)
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.08)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          flexDirection: "column",
        }}
      >
        <div
          style={{
            marginBottom: 18,
            width: 56,
            height: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width={48} height={48} viewBox="0 0 50 50">
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="#2684ff"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray="31.4 94.2"
              style={{ transformOrigin: 'center', animation: 'spin 1.2s linear infinite' }}
            />
            <style>
              {`
      @keyframes spin {
        100% { transform: rotate(360deg); }
      }
    `}
            </style>
          </svg>
        </div>
        <StatusText />
      </div>
    );


  if (questions.length === 0 && !interviewComplete)
    return <div style={{ padding: 20 }}>No questions available for this interview.</div>;

  if (interviewComplete)
    return (
      <div style={styles.root}>
        <header style={{ ...styles.header, position: "sticky", top: 0, zIndex: 10 }}>
          <div style={styles.headerLeft}>
            <span style={styles.avatar}>🧑‍💼</span>
            <div>
              <div style={styles.userName}>{userName}</div>
              <div style={styles.jobLabel}>
                {job.jobrole || ""}
                {job.location ? `, ${job.location}` : ""}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#5372c5",
                  margin: "9px 0 0 0",
                }}
              >
                Interview Complete!
              </div>
            </div>
          </div>
          <div style={styles.progressBarArea}>
            <div style={{ ...styles.progressBar, width: 170, marginTop: 8 }}>
              <div style={{ ...styles.progressFill, width: "100%", background: "#a5f5c2" }} />
            </div>
          </div>
          {/* Timer below progress bar on interview complete page (optional) */}
        </header>
        <main
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div style={{ marginBottom: 14 }}>
            <svg
              width={60}
              height={60}
              viewBox="0 0 46 46"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx={23} cy={23} r={23} fill="#22ba73" />
              <path
                d="M13 24.5l6.8 6 13-13"
                stroke="white"
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 style={{ fontWeight: 700, color: "#244356", fontSize: "1.3rem", margin: 0 }}>
            Thank you!
          </h2>
          <div
            style={{
              color: "#7ba0c5",
              marginTop: 9,
              marginBottom: 0,
              fontSize: "1.04rem",
              textAlign: "center",
            }}
          >
            You have finished all questions. We’ll be in touch soon.
          </div>
        </main>
      </div>
    );

  const answeredCount = questions.filter((q) => q.answer && q.answer.trim() !== "").length;
  const progressPercent = Math.min((answeredCount / questions.length) * 100, 100);

  console.log(
    `Rendering question ${currentIndex}`,
    "answer:",
    questions[currentIndex]?.answer,
    "response:",
    response,
    "textarea disabled:",
    !!questions[currentIndex]?.answer
  );

  return (
    <div style={styles.root}>
      <style>
        {`
          @keyframes blink {
            0% { opacity: 1; }
            50% { opacity: 0.2; }
            100% { opacity: 1; }
          }
          @keyframes spin {
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Sticky Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.avatar}>🧑‍💼</span>
          <div>
            <div style={styles.userName}>{userName}</div>
            <div style={styles.jobLabel}>
              {job.jobrole || ""}
              {job.location ? `, ${job.location}` : ""}
            </div>
          </div>
        </div>
        <div style={styles.progressBarArea}>
          <div style={{ ...styles.progressBar, width: 170, marginTop: 8 }}>
            <div style={{ ...styles.progressFill, width: `${progressPercent}%` }} />
          </div>
          {/* Timer below progress bar */}

        </div>
      </header>

      {/* Sticky question progress bar */}
      {/* Sticky question progress bar */}
      <div
        style={{
          ...styles.stickyQInfoBar,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={styles.stickyQuestionCount}>
          Question {currentIndex + 1} / {questions.length}
        </span>
        {!interviewComplete && (
          <div
            style={{
              ...timerBoxStyle,
              marginRight: -30,
              marginTop: 0,
              marginBottom: 0,
              color: isLowTime ? "#ff1a1a" : "#224979",
              borderColor: isLowTime ? "#ff1a1a" : "#224979",
              animation: isLowTime ? "blink 1s step-start infinite" : "none",
            }}
          >
            Time remaining: {Math.floor(timeLeft / 60).toString().padStart(2, "0")}:
            {(timeLeft % 60).toString().padStart(2, "0")}
            {extraTimeGiven && (
              <span style={{ color: "#e74c3c", fontWeight: 700, fontSize: 12, marginLeft: 8 }}>
                (1 min extra time granted)
              </span>
            )}
          </div>
        )}
      </div>


      {/* Question and answer area */}
      <main
        style={{
          flex: 1,
          width: "100vw",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          background: "transparent",
          padding: 0,
          margin: 0,
        }}
      >
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "stretch",
            width: "100%",
            height: "100%",
            background: "#fff",
            boxShadow: "0 2px 16px #22519711",
            padding: 0,
            margin: 0,
          }}
        >
          <div
            style={{
              fontWeight: 500,
              color: "#2d4f8a",
              fontSize: "30px",
              padding: "22px 60px 12px 48px",
              minHeight: 85,
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {questions[currentIndex]?.question}
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              padding: "0 60px 0 48px",
              marginBottom: 6,
              minHeight: 0,
            }}
          >
            <textarea
              ref={inputRef}
              style={{
                width: "100%",
                flex: 1,
                fontSize: "1.13rem",
                padding: "18px 18px",
                resize: "vertical",
                outline: "none",
                borderRadius: 7,
                border: "1.3px solid #000000ff",
                boxSizing: "border-box",
                whiteSpace: "pre-wrap",
                overflowWrap: "break-word",
                lineHeight: 1.5,
                background: questions[currentIndex]?.answer ? "#f4f6fa" : "#fff",
                color: "#23233b",
                minHeight: "180px",
                maxHeight: "330px",
                marginBottom: 0,
              }}
              value={response}
              onChange={(e) => {
                if (!questions[currentIndex]?.answer) {
                  setResponse(e.target.value);
                  responseRef.current = e.target.value;
                } else {
                  setResponse(questions[currentIndex].answer);
                  responseRef.current = questions[currentIndex].answer;
                }
              }}
              spellCheck={false}
              disabled={!!questions[currentIndex]?.answer}
              readOnly={!!questions[currentIndex]?.answer}
              placeholder="Enter your answer..."
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "0 48px 46px 48px",
            }}
          >
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              style={{
                ...styles.sendBtn,
                backgroundColor: currentIndex === 0 ? "#bfd4f9" : "#2684ff",
                cursor: currentIndex === 0 ? "not-allowed" : "pointer",
              }}
            >
              Previous
            </button>

            {!questions[currentIndex]?.answer ? (
              <button
                onClick={handleNext}
                disabled={response.trim() === ""}
                style={{
                  ...styles.sendBtn,
                  backgroundColor: response.trim() === "" ? "#2684ff80" : "#2684ff",
                }}
              >
                {questions.length === 8 && currentIndex === 7 ? "Submit" : "Next"}
              </button>
            ) : (
              <button
                onClick={() => {
                  if (currentIndex < questions.length - 1) setCurrentIndex(currentIndex + 1);
                }}
                disabled={currentIndex === questions.length - 1}
                style={{
                  ...styles.sendBtn,
                  backgroundColor: currentIndex === questions.length - 1 ? "#bfd4f9" : "#2684ff",
                  cursor: currentIndex === questions.length - 1 ? "not-allowed" : "pointer",
                }}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </main>
      {/* Sending overlay – put it AFTER <main> but still inside root <div> */}
      {sending && !interviewComplete && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            flexDirection: "column",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: "20px 28px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
              minWidth: 260,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "3px solid #d0e2ff",
                borderTopColor: "#2684ff",
                animation: "spin 0.9s linear infinite",
              }}
            />
            <div style={{ fontWeight: 600, color: "#244356" }}>
              Generating next question...
            </div>
            <div style={{ fontSize: 13, color: "#7188a6" }}>
              This may take a few seconds.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// StatusText component unchanged
function StatusText() {
  const statusPhrases = [
    "Analyzing",
    "Curating interview questions...",
    "Generating question set...",
    "Preparing personalized assessment...",
  ];
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((i) => (i + 1) % statusPhrases.length);
    }, 1600);
    return () => clearInterval(interval);
  }, []);
  return (
    <div
      style={{
        fontSize: 18,
        fontWeight: 600,
        color: "#244356",
        marginTop: 14,
        textAlign: "center",
        minHeight: 32,
        transition: "color 0.3s",
      }}
    >
      {statusPhrases[index]}
    </div>
  );
}
